import _get from 'lodash/get';
import {
  useAssetCategoriesSearchListLazyQuery,
  useAssetsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  useUsersSearchListLazyQuery,
} from 'graphql-common';
import { useEdmDocumentationsLazyQuery } from '@lib/graphql-common';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getAssetCategoriesLoadOptions as getAssetCategoriesLoadOptionsFn } from 'utils/fetch/searchLists/assetCategoriesSearchList';
import AssetFieldNames from '@lib/enums/fieldNames/assetFieldNames';
import { getSiteAreasLoadOptions as getSiteAreasLoadOptionsFn } from 'utils/fetch/searchLists/siteAreasSearchList';
import { getEmailRecipientsLoadOptions } from 'utils/fetch/searchLists/usersSearchList';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import { getAssetsLoadOptions as getAssetsLoadOptionsFn } from 'utils/fetch/searchLists/assetsSearchList';
import { getDocumentationsLoadOptions as getDocumentationsLoadOptionsFn } from 'utils/fetch/searchLists/documentationsSearchList';

export type UseSearchListQueriesResult = {
  getAssetsLoadOptions: GetLoadOptions;
  getAssetCategoriesLoadOptions: GetLoadOptions;
  getDocumentationsLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getUsersLoadOptions: GetLoadOptions;
};

export default function useSearchListQueries(): UseSearchListQueriesResult {
  // Assets
  const [fetchAssetsLazyQuery] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getAssetsLoadOptions = () =>
    getAssetsLoadOptionsFn(fetchAssetsLazyQuery);

  // Documentations
  const [fetchDocumentationsLazyQuery] = useEdmDocumentationsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getDocumentationsLoadOptions = () =>
    getDocumentationsLoadOptionsFn(fetchDocumentationsLazyQuery);

  // Asset categories
  const [fetchAssetCategoriesLazyQuery] = useAssetCategoriesSearchListLazyQuery(
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const getAssetCategoriesLoadOptions = () =>
    getAssetCategoriesLoadOptionsFn(fetchAssetCategoriesLazyQuery);

  // Site query
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSitesLoadOptions: GetLoadOptions = () =>
    getSitesLoadOptionsFn(fetchSitesSearchListLazyQuery, undefined, {
      includeSector: true,
    });

  // Site area query
  const [fetchSiteAreasSearchListLazyQuery] = useSiteAreasSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSiteAreasLoadOptions: GetLoadOptions = (name, values) => {
    const selectedSiteId = _get(values, [AssetFieldNames.Site, 'value']);
    return getSiteAreasLoadOptionsFn(
      fetchSiteAreasSearchListLazyQuery,
      selectedSiteId,
    );
  };

  // Users
  const [fetchUsersSearchListLazyQuery] = useUsersSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getUsersLoadOptions = () =>
    getEmailRecipientsLoadOptions(fetchUsersSearchListLazyQuery);

  return {
    getAssetsLoadOptions,
    getAssetCategoriesLoadOptions,
    getDocumentationsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getUsersLoadOptions,
  };
}
